/**
 * Die Elternzeile einer Sammelsendung im Bestellboard (Task 6, Spec
 * `2026-09-29-sammelsendung-elternzeile-design.md`, Abschnitt "Board"; Aggregatzellen seit dem Plan
 * `2026-10-02-sammelsendung-aggregat-zeile.md`).
 *
 * ================= Eine Zelle je sichtbarer Spalte =================
 *
 * Die Zeile hat so viele `<td>` wie die Kinderzeile (`Bestellzeile`), in derselben Reihenfolge. Die
 * ERSTE Zelle traegt den Kopf (Aufklappknopf, Oeffnen-Knopf); jede weitere zeigt den aggregierten Wert
 * der Kinder DIESES Bereichs (`sammelsendungAggregat.ts`, gezeichnet in `AggregatZellen.tsx`
 * und als `zellen` hereingereicht). Die Aggregatzellen sind nur lesend und reagieren auf KEINEN Klick:
 * ein Klick-Handler auf der Zelle risse die Bedienung in zwei Wege (der Aufklappknopf steht in der
 * ersten Zelle, ein Zellenklick dort bubbelte doppelt) und wuerde Textmarkieren zum Kopieren stoeren.
 * Aufgeklappt wird allein ueber den Knopf. EINZIGE Ausnahme (Plan 2026-10-31, Task 6): die Faelligkeitszelle -
 * `BoardSeite` reicht dort `SammelFaelligkeitZelle` herein, die mit dem Recht `edit_orders` ein Datumsfeld oeffnet
 * und ihren Klick selbst anhaelt (kein Aufklappen, kein Drawer). Diese Zeile weiss davon nichts.
 *
 * ================= Der Kopf: zwei Zeilen =================
 *
 * Die erste Spalte ist schmal (Vorgabe 190 px, mindestens 100 px). Der Kopf steht deshalb zweizeilig
 * im Aufklappknopf: Zeile 1 Pfeil, Symbol, "Sammelsendung" (und "· Nr", s. u.), Zeile 2 klein und
 * gedaempft "n Bestellungen" bzw. "n von m". Die Mitgliederzahl schrumpft nie (`flex: none`, kein
 * `overflow`); nur Titel und Nummer kuerzen mit Ellipsis (voller Text im `title`). Umgebrochen wird nur
 * an Wortgrenzen und zwischen den Teilen, nie mitten in einem Wort oder einer Nummer; die Nummer darf
 * in eine eigene Zeile rutschen. Der Oeffnen-Knopf (24 x 24 px) liegt in der Ecke, Zeile 1 haelt rechts
 * 32 px frei.
 * Lieferant, Status und Gesamtbetrag haengen an Zeile 2, wenn ihre Spalte nicht als eigene Zelle
 * sichtbar ist (ausgeblendet oder selbst die erste Spalte) - "2 Bestellungen · Import · $ 61.940,20";
 * sichtbar als eigene Zelle stehen sie nie doppelt im Kopf.
 *
 * Die Sendungsnummer steht in der Spalte "Trackingnummer". Ist diese ausgeblendet (oder ist sie selbst
 * die erste Spalte), bleibt die Nummer im Kopf sichtbar; sonst blendet das CSS sie dort aus - ausser auf
 * schmalen Ansichten, wo der Chip `Sammelsendung · <Nr>` mit Mitgliedszahl erhalten bleibt (Spec).
 *
 * ================= Zwei getrennte Knoepfe, ein Zustand =================
 *
 * - Der AUFKLAPPKNOPF ist ein natives `<button>` mit `aria-expanded`. Enter, Leertaste und Klick
 *   loesen beim nativen Knopf den `click` aus - hier gibt es deshalb KEINEN eigenen Tastatur-
 *   Handler (ein zusaetzliches `onKeyDown` kippte den Zustand doppelt). Er navigiert nie.
 * - Der NAVIGATIONSKNOPF ruft `onSammelsendungOeffnen(shipmentId)` und aendert den Aufklappzustand
 *   nicht. Der Drawer dahinter ist Task 7.
 *
 * Beide tragen einen festen zugaenglichen Namen (`aria-label`), der nicht mit dem Zustand
 * wechselt: der Zustand steht in `aria-expanded`, nicht im Namen. Weil `aria-label` den Inhalt des
 * Knopfes fuer Hilfstechnik verdraengt, verweist `aria-describedby` auf die Anzahl sowie Lieferant,
 * Status und Betrag - dort, wo sie stehen: in ihrer eigenen Zelle oder, ohne diese Spalte, in Zeile 2
 * des Kopfes. So bleiben sie als Beschreibung erreichbar (wie bei der Kindzeile).
 *
 * Der Aufklappzustand gehoert `BoardSeite` (`offen`/`onUmschalten`, je Bereich und Sendung): nur
 * dort ist bekannt, welche Kinder sichtbar sind (Shift-Bereichsauswahl), und er ueberlebt so ein
 * Neu-Mounten der Zeile. Die Mehrfachauswahl beruehrt ihn nie - eine Strg-/Shift-Auswahl der Kinder
 * klappt die Klammer weder auf noch zu.
 *
 * Die Kinder kommen als `children` (fertige `Bestellzeile`n) und stehen nur im Baum, solange die
 * Klammer offen ist. Das Layout liegt in `styles/neubau/sammelsendung.css` (`bestand.css` ist
 * zeichengleich an `index.html` gebunden und bleibt unberuehrt).
 */
import { useId } from "react";
import type { ReactNode } from "react";
import { Ikone } from "../Ikone.tsx";
import { MONO_KEYS } from "./Bestellzeile.tsx";
import type { KopfWerte } from "./AggregatZellen.tsx";

/** Spalten, deren Zelle die Beschreibung des Aufklappknopfs mittraegt. */
const BESCHREIBENDE_SPALTEN = ["supplier", "status", "value"] as const;

export interface SammelsendungszeileProps {
  shipmentId: string;
  dsvShipmentNo: string;
  /** Mitglieder der Sendung insgesamt. */
  memberCount: number;
  /** Davon in diesem Bereich sichtbar; kleiner als `memberCount` -> Chip "n von m". */
  sichtbareAnzahl: number;
  offen: boolean;
  onUmschalten: () => void;
  /** Schluessel der sichtbaren Board-Spalten, in Reihenfolge der Kinderzeile. */
  spalten: readonly string[];
  /** Fertige Aggregatinhalte je Spaltenschluessel; die erste Spalte traegt stets den Kopf. */
  zellen: Readonly<Record<string, ReactNode>>;
  /** Zentrale Werte fuer Zeile 2 des Kopfes, soweit ihre Spalte keine eigene Zelle hat. */
  kopfWerte?: KopfWerte;
  onSammelsendungOeffnen?: (shipmentId: string) => void;
  children?: ReactNode;
}

export function Sammelsendungszeile({
  shipmentId,
  dsvShipmentNo,
  memberCount,
  sichtbareAnzahl,
  offen,
  onUmschalten,
  spalten,
  zellen,
  kopfWerte = {},
  onSammelsendungOeffnen,
  children,
}: SammelsendungszeileProps) {
  const kennung = useId();
  const anzahlId = `${kennung}-anzahl`;
  const zellenId = (schluessel: string) => `${kennung}-z-${schluessel}`;
  // Aggregatzellen = alle Spalten nach der ersten (dort steht der Kopf).
  const aggregatSpalten = spalten.slice(1);
  // Ohne eigene Zelle zieht der Wert in Zeile 2 des Kopfes; mit eigener Zelle steht er nur dort.
  const imKopf = BESCHREIBENDE_SPALTEN.filter((schluessel) => !aggregatSpalten.includes(schluessel) && kopfWerte[schluessel]);
  const inZelle = BESCHREIBENDE_SPALTEN.filter((schluessel) => aggregatSpalten.includes(schluessel));
  const beschreibung = [anzahlId, ...inZelle.map(zellenId), ...imKopf.map((k) => zellenId(k))].join(" ");
  const nummerInZelle = aggregatSpalten.includes("dsvShipmentNo");
  return (
    <>
      <tr
        className={`sammelsendung-zeile${offen ? "" : " collapsed"}`}
        data-shipment-id={shipmentId}
      >
        <td>
          <div className="sammelsendung-kopf">
            <button
              type="button"
              className="sammelsendung-toggle"
              aria-expanded={offen ? "true" : "false"}
              aria-label={`Sammelsendung ${dsvShipmentNo} aufklappen`}
              aria-describedby={beschreibung}
              onClick={onUmschalten}
            >
              <span className="sammelsendung-zeile1">
                <span className="sammelsendung-pfeil collapse-pair" aria-hidden="true">
                  <Ikone name="chevrons-right" className="collapse-closed" />
                  <Ikone name="chevrons-down" className="collapse-open" />
                </span>
                <Ikone name="combine" className="sammelsendung-klammer" />
                <span className="sammelsendung-titel" title="Sammelsendung">Sammelsendung</span>
                <span className={`sammelsendung-kopfnummer${nummerInZelle ? " sammelsendung-kopfnummer-nur-schmal" : ""}`}>
                  <span className="sammelsendung-trenner" aria-hidden="true">{" · "}</span>
                  <span className="sammelsendung-nummer" title={dsvShipmentNo}>{dsvShipmentNo}</span>
                </span>
              </span>
              <span className="sammelsendung-zeile2">
                <span id={anzahlId} className="sammelsendung-anzahl">{sichtbareAnzahl < memberCount ? `${sichtbareAnzahl} von ${memberCount}` : memberCount} Bestellungen
                </span>
                {imKopf.map((schluessel) => (
                  <span key={schluessel} id={zellenId(schluessel)} className="sammelsendung-kopfwert">
                    <span className="sammelsendung-trenner" aria-hidden="true">{" · "}</span>
                    {kopfWerte[schluessel]}
                  </span>
                ))}
              </span>
            </button>
            <button
              type="button"
              className="sammelsendung-oeffnen"
              aria-label={`Sammelsendung ${dsvShipmentNo} öffnen`}
              title="Sammelsendung öffnen"
              onClick={() => onSammelsendungOeffnen?.(shipmentId)}
            >
              <Ikone name="external-link" />
              <span aria-hidden="true">Öffnen</span>
            </button>
          </div>
        </td>
        {aggregatSpalten.map((schluessel) => (
          <td key={schluessel} id={zellenId(schluessel)} className={MONO_KEYS.has(schluessel) ? "mono" : ""}>
            {zellen[schluessel] ?? null}
          </td>
        ))}
      </tr>
      {offen ? children : null}
    </>
  );
}
